import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgbCarousel } from '@ng-bootstrap/ng-bootstrap/carousel/carousel';
import { LAUNCH_CALLER, LaunchDialogService } from '@spartacus/storefront';
import { Observable, BehaviorSubject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Article, Image, Product } from '../../../../core/model';
import { ImageModalData } from '../../../../shared/components/image-modal/image-modal.component';
import { BrowserService } from '../../../../shared/services/browser/browser.service';
import { PrincipalConfigurationService } from '../../../../core/user';

@Component({
  selector: 'py-pdp-image-gallery',
  templateUrl: './pdp-image-gallery.component.html',
  styleUrls: ['./pdp-image-gallery.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PdpImageGalleryComponent implements OnInit, OnDestroy {
  @ViewChild('carousel', { static: true }) carouselRef: NgbCarousel;

  private subscription = new Subscription();
  private _product: Product;
  private _articleImages: Image[] = [];
  private _galleryImages: Image[] = [];

  enableProductOutletBadgeOnlyIfAllArticlesAreOutlet$: Observable<boolean>;
  article$ = new BehaviorSubject<Article>(undefined);
  alternativeText: string;
  isEdgeBrowser: boolean = false;
  isSliding = false;

  @Input()
  set galleryImages(images: Image[]) {
    this._galleryImages = images;
  }

  @Input()
  set product(product: Product) {
    this._product = product;

    this.setAltText(this.article, product);
  }

  get product(): Product {
    return this._product;
  }

  @Input()
  set article(article: Article) {
    this.setAltText(article, this.product);

    this.article$.next(article);
  }

  get article(): Article {
    return this.article$.getValue();
  }

  get images(): Image[] {
    const urls = [];
    const images = [];

    [...this._galleryImages, ...this._articleImages].forEach((image) => {
      if (!image?.url || urls.includes(image.url)) {
        return;
      } else {
        images.push(image);
        urls.push(image.url);
      }
    });

    return images;
  }

  activeSlideId = 0;
  thumbsPerRow = 5;

  constructor(
    private cd: ChangeDetectorRef,
    private browserService: BrowserService,
    private launchDialogService: LaunchDialogService,
    private principalConfigurationService: PrincipalConfigurationService
  ) {
    this.isEdgeBrowser = this.browserService.isEdgeBrowser();
    this.enableProductOutletBadgeOnlyIfAllArticlesAreOutlet$ = this.principalConfigurationService.isEnabled(
      'enableProductOutletBadgeOnlyIfAllArticlesAreOutlet'
    );
  }

  private setAltText(article: Article, product: Product): void {
    const newAltText = article?.articleName || article?.productName || product?.name;

    if (newAltText !== this.alternativeText) {
      this.alternativeText = newAltText;
    }
  }

  ngOnInit(): void {
    this.subscription.add(
      this.article$.pipe(debounceTime(10)).subscribe((article) => {
        const images: Image[] = article?.image || article?.articleGroupImage ? [article.image || article.articleGroupImage] : [];
        if (images && images.length > 0) {
          this._articleImages = images;
          // Navigate to article image
          const articleIndex = this.images.findIndex((image) => image.url === images[0].url);
          if (articleIndex !== -1) {
            this.navigateToSlide(articleIndex);
          }
        } else {
          this._articleImages = [];
          this.navigateToSlide(0);
        }
        this.cd.markForCheck();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  navigateToSlide(slideId: number) {
    if (!this.isSliding && this.carouselRef?.select) {
      this.carouselRef.select(slideId.toString());
      this.activeSlideId = slideId;
    }
    this.cd.detectChanges();
  }

  onMainImageClick(imageIndex: number): void {
    const modalData: ImageModalData = {
      alternativeText: this.alternativeText,
      images: this.images,
      initialActiveSlideIndex: imageIndex,
    };

    this.launchDialogService.openDialogAndSubscribe(LAUNCH_CALLER.IMAGE, undefined, modalData);
  }
}
